<template>
    <div class="ref020-detail-header">
        <!-- 좌측 상품 이미지 — 56x56 고정 박스 (안건 43: 2-row 높이 맞춤) -->
        <div class="ref020-detail-header-img-wrap">
            <img v-if="imgUrl"
                 :src="imgUrl"
                 :alt="prodName || ''"
                 class="ref020-detail-header-img"
                 width="56"
                 height="56"
                 @error="onImgError" />
            <span v-else class="ref020-detail-header-img-placeholder">
                <i class="material-icons">image</i>
            </span>
        </div>

        <!-- 정보 영역 — 안건 80 (2026-05-14): 1행=상품정보 (페이지 주체), 2행=하우스/트래킹 (운영 식별 정보).
             기존 안건 43 (시안 6) 의 1↔2 swap. 상태 prefix 는 1행 좌측 유지. 회원사 정보는 1행 우측 끝 유지.
             강조 항목 (수취인/운송장) 은 색 변경 (안건 80-d: 아이콘 + 라벨 파랑 #0f7ae5 — bold 회귀 없이 시각 차별).
             빈값 정책: 모든 슬롯 항상 렌더 (위치 고정 — 시선 안정) + 옅은 dash placeholder. -->
        <div class="ref020-detail-header-body">
            <!-- 1행 (상품 정보): [상태 prefix] · #상품번호 · 상품명 · 색상 · 사이즈 · 수량 · 구입단가 · ... · (우측 끝) 회원사 -->
            <div class="ref020-detail-header-row ref020-detail-header-row-product">
                <span v-if="stateText"
                      class="ref020-detail-header-state-prefix"
                      :class="stateBadgeClass"
                      :title="stateBadgeTitle">
                    <i class="material-icons">{{ stateClass === 'danger' ? 'warning_amber' : 'local_shipping' }}</i>
                    <span class="ref020-detail-header-state-text">{{ stateText }}</span>
                    <span v-if="stateChangeDate" class="ref020-detail-header-state-date">{{ stateChangeDate }}</span>
                </span>

                <span v-if="prodId" class="ref020-detail-header-item ref020-detail-header-meta-id"
                      :title="'상품번호: #' + prodId">
                    <i class="material-icons">tag</i>
                    <span class="ref020-detail-header-label">상품번호</span><span class="ref020-detail-header-prod-prefix">#</span>{{ prodId }}
                </span>

                <template v-if="prodName">
                    <span v-if="prodId" class="ref020-detail-header-sep">·</span>
                    <span class="ref020-detail-header-item ref020-detail-header-prod-name"
                          :title="'상품명: ' + prodName">
                        {{ croppedName }}
                    </span>
                </template>

                <template v-if="color">
                    <span v-if="prodId || prodName" class="ref020-detail-header-sep">·</span>
                    <span class="ref020-detail-header-item" :title="'색상: ' + color">
                        <i class="material-icons">palette</i>
                        <span class="ref020-detail-header-label">색상</span>{{ color }}
                    </span>
                </template>

                <template v-if="size">
                    <span v-if="prodId || prodName || color" class="ref020-detail-header-sep">·</span>
                    <span class="ref020-detail-header-item" :title="'사이즈: ' + size">
                        <i class="material-icons">straighten</i>
                        <span class="ref020-detail-header-label">사이즈</span>{{ size }}
                    </span>
                </template>

                <template v-if="amount">
                    <span v-if="prodId || prodName || color || size" class="ref020-detail-header-sep">·</span>
                    <span class="ref020-detail-header-item" :title="'수량: ' + amount + '개'">
                        <span class="ref020-detail-header-label">수량</span>{{ amount }}개
                    </span>
                </template>

                <template v-if="unitPrice">
                    <span v-if="prodId || prodName || color || size || amount" class="ref020-detail-header-sep">·</span>
                    <span class="ref020-detail-header-item" :title="'구입단가: ' + unitPriceText">
                        <span class="ref020-detail-header-label">구입단가</span><span v-html="unitPrice"></span>
                    </span>
                </template>

                <!-- 회원사 — 안건 80 (2026-05-14): 분기 없이 닉네임만 (사서함은 좌측 사이드바 어드민 카드에서 확인).
                     userNick 만 사용. userUrl 있으면 anchor, 없으면 span. 사서함(userMailbox) prop 폐기 흐름.
                     안건 91 (2026-05-18): restrictShipout=true 시 회원사 닉네임 옆 라벨 노출 (총괄관리자 인지).
                     안건 91 후속 3 (2026-05-18 사용자 결정): 출고제한 라벨은 anchor 밖으로 분리 (회원사 URL 링크에서 제외 — 클릭 시 회원사 페이지 이동 차단). -->
                <template v-if="userNick">
                    <a v-if="userUrl"
                       :href="userUrl"
                       class="ref020-detail-header-item ref020-detail-header-member-inline"
                       :title="'회원사: ' + userNick"
                       target="_blank">
                        <i class="material-icons">home</i>
                        <span class="ref020-detail-header-label">회원사</span>{{ userNick }}
                    </a>
                    <span v-else
                          class="ref020-detail-header-item ref020-detail-header-member-inline"
                          :title="'회원사: ' + userNick">
                        <i class="material-icons">home</i>
                        <span class="ref020-detail-header-label">회원사</span>{{ userNick }}
                    </span>
                    <!-- 출고제한 / 배송등록제한 라벨 (anchor 밖) — 닉네임/anchor 옆 inline 위치 유지하되 클릭 영역 분리.
                         동일 디자인 (.ref020-restrict-shipout-label SSOT) — 설정된 항목만 노출. -->
                    <span v-if="restrictShipout"
                          class="ref020-restrict-shipout-label"
                          uk-tooltip="title: 미결제 등의 사유로 출고제한 되었습니다.; pos: top"><i class="material-icons">block</i>출고제한</span>
                    <span v-if="restrictRegister"
                          class="ref020-restrict-shipout-label"
                          uk-tooltip="title: 미결제 등의 사유로 배송등록제한 되었습니다.; pos: top"><i class="material-icons">block</i>등록제한</span>
                </template>
            </div>

            <!-- 2행 (하우스/트래킹 정보): 수취인(강조) · 배송코드(강조) · 운송장(강조) · 트래킹 · HAWB
                 안건 80 정정 (2026-05-14): 배송코드 강조 추가 + 운송장/트래킹 순서 교체 (운송장 → 트래킹).
                 안건 50-2 (운송장 → 트래킹) 의 좌→우 순서 정정. 안건 80 사용자 결정.
                 강조 = 값 bold + 폰트 살짝 큼 (아이콘/라벨은 일반 톤). -->
            <div class="ref020-detail-header-row ref020-detail-header-row-house">
                <template v-if="recipientName">
                    <span class="ref020-detail-header-item ref020-detail-header-emph"
                          :title="'수취인: ' + recipientName">
                        <i class="material-icons">person</i>
                        <span class="ref020-detail-header-label">수취인</span>{{ recipientName }}
                    </span>
                </template>

                <template v-if="houseCode">
                    <span v-if="recipientName" class="ref020-detail-header-sep">·</span>
                    <span class="ref020-detail-header-item ref020-detail-header-emph"
                          :title="'배송코드: ' + houseCode">
                        <i class="material-icons">qr_code_2</i>
                        <span class="ref020-detail-header-label">배송코드</span>{{ houseCode }}
                    </span>
                </template>

                <template v-if="trackingNo">
                    <span v-if="recipientName || houseCode" class="ref020-detail-header-sep">·</span>
                    <span class="ref020-detail-header-item ref020-detail-header-emph"
                          :title="'운송장: ' + trackingNo">
                        <i class="material-icons">local_shipping</i>
                        <span class="ref020-detail-header-label">운송장</span><span v-if="trackingNoLinkHtml" v-html="trackingNoLinkHtml"></span><template v-else>{{ trackingNo }}</template>
                        <copy tooltip="운송장 복사하기" :copy-text="trackingNo"></copy>
                    </span>
                </template>

                <template v-if="prodTrackingNo">
                    <span v-if="recipientName || houseCode || trackingNo" class="ref020-detail-header-sep">·</span>
                    <span class="ref020-detail-header-item"
                          :title="'트래킹: ' + prodTrackingNo">
                        <i class="material-icons">route</i>
                        <span class="ref020-detail-header-label">트래킹</span><span v-if="prodTrackingNoLinkHtml" v-html="prodTrackingNoLinkHtml"></span><template v-else>{{ prodTrackingNo }}</template>
                        <copy tooltip="트래킹 복사하기" :copy-text="prodTrackingNo"></copy>
                    </span>
                </template>

                <template v-if="showHawb">
                    <span v-if="recipientName || houseCode || trackingNo || prodTrackingNo" class="ref020-detail-header-sep">·</span>
                    <span class="ref020-detail-header-item" :title="'HAWB: ' + hawb">
                        <i class="material-icons">flight</i>
                        <span class="ref020-detail-header-label">HAWB</span>{{ hawb }}
                        <copy tooltip="HAWB 복사하기" :copy-text="hawb"></copy>
                    </span>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
// REF-020 hot-fix #20 안건 42·43·80 — 상품상세 페이지 전용 헤딩 컴포넌트
//
// 안건 43 (2026-05-11 시안 6 적용):
//   · 좌측 56x56 이미지 (2-row 맞춤)
//   · 빈값 정책 D: 모든 슬롯 항상 렌더, 빈 값은 옅은 dash (—) — 시선 안정 + layout shift 방지
//
// 안건 80 (2026-05-14 — 사용자 결정으로 1↔2 swap):
//   · 페이지 의도 정합 — 상품상세 페이지의 주체 = 상품. 1행에 상품정보 배치 = 즉시 인식
//   · 1행 (상품 정보, 페이지 주체): [상태 prefix 배지] + #상품번호 + 상품명 + 색상 + 사이즈 + 수량 + 단가 + (우측 끝) 회원사
//   · 2행 (하우스 / 트래킹, 운영 식별): 수취인(강조) + 배송코드 + 트래킹 + 운송장(강조) + HAWB
//   · 강조 (수취인 / 운송장) 색 변경 — 아이콘 + 라벨 파랑 #0f7ae5 (상태 normal pill 톤 일관)
//   · 회원사 정보 = 닉네임만 (분기 없이) — 사서함은 좌측 사이드바 어드민 카드에서 확인
//   · userMailbox prop 폐기 (호환 위해 prop 정의 유지, 템플릿 미사용)
export default {
    name: 'app-prods-detail-header',
    props: {
        recipientName: { type: String, default: null },
        houseCode: { type: String, default: null },
        // 운송장 (houses.blno) — 안건 43 후속에서 prop 이름은 trackingNo 로 유지 (호환).
        trackingNo: { type: String, default: null },
        // 안건 50 (2026-05-11): 운송장 외부 추적 anchor HTML (blnoLink 결과)
        trackingNoLinkHtml: { type: String, default: null },
        // 안건 50 (2026-05-11): 트래킹번호 (prods.tracking_no) — 운송장(blno) 과 별개 칼럼
        prodTrackingNo: { type: String, default: null },
        prodTrackingNoLinkHtml: { type: String, default: null },
        prodId: { type: [Number, String], default: null },
        prodName: { type: String, default: null },
        stateText: { type: String, default: null },
        stateClass: { type: String, default: 'normal' },
        cropLength: { type: Number, default: 40 },
        // 상품 spec
        imgUrl: { type: String, default: null },
        color: { type: String, default: null },
        size: { type: String, default: null },
        amount: { type: [Number, String], default: null },
        unitPrice: { type: String, default: null },
        // 부가 — 안건 80 (2026-05-14): userMailbox prop 폐기. 회원사 정보는 닉네임만 (사서함은 사이드바 어드민 카드에서 확인).
        //   호환을 위해 prop 자체는 보존하되 템플릿에서 사용 안 함 — 호출측 (Blade) 정합은 점진적 정리 가능.
        userNick: { type: String, default: null },
        userMailbox: { type: String, default: null }, // deprecated (안건 80) — 템플릿 미사용. 호출측 정합 후 제거 예정
        userUrl: { type: String, default: null },
        // 안건 91 (2026-05-18): 회원사 출고제한 + 배송등록제한 정보 — true 시 회원사 닉네임 옆 라벨 노출
        restrictShipout: { type: Boolean, default: false },
        restrictRegister: { type: Boolean, default: false },
        hawb: { type: String, default: null },
        // 안건 43 후속: 배송단계 변경일 (state badge 옆에 표시)
        stateChangeDate: { type: String, default: null },
    },
    computed: {
        croppedName() {
            if (!this.prodName) return '';
            if (this.prodName.length <= this.cropLength) return this.prodName;
            return this.prodName.substring(0, this.cropLength) + '…';
        },
        stateBadgeClass() {
            return 'ref020-detail-header-state-' + (this.stateClass || 'normal');
        },
        stateBadgeTitle() {
            let t = '상태: ' + (this.stateText || '없음');
            if (this.stateChangeDate) t += ' (변경일: ' + this.stateChangeDate + ')';
            return t;
        },
        // HAWB 가 운송장과 동일하면 노출 X (실질 정보 동일)
        showHawb() {
            return !!(this.hawb && this.hawb !== this.trackingNo);
        },
        unitPriceText() {
            if (!this.unitPrice) return '';
            return this.unitPrice.replace(/<[^>]*>/g, '').trim();
        },
    },
    methods: {
        onImgError(e) {
            e.target.style.visibility = 'hidden';
        },
    },
};
</script>

<style scoped>
.ref020-detail-header {
    display: flex;
    gap: 1rem;
    background: #f6f8fa; /* 안건 80 정정 (2026-05-14): 컨텐츠(#fff) 와 구분 + 서브헤더(#fafbfc) 보다 한 단계 진하게 → 헤딩 sticky 인지 명도 계층 (서브헤더 < 헤딩 < 컨텐츠) */
    border-bottom: 1px solid #e5e7eb;
    padding: 0.75rem 1.25rem;
    align-items: center;
}

/* 안건 47 (2026-05-11): 스크롤 시 상단 sticky — 가장 큰 화면 (≥ 1280px) 에서만 적용.
   작은 화면에서는 메인 컨텐츠가 가려져서 sticky 적용 X (사용자 결정).
   안건 80 정정 (2026-05-14): box-shadow 강화 (0 1px 0 0.02 → 0 2px 4px 0.06) — 부유감 명확화 */
@media (min-width: 1280px) {
    .ref020-detail-header {
        position: sticky;
        top: 0;
        z-index: 10;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
    }
}

/* 좌측 상품 이미지 — 56x56 (안건 43) */
.ref020-detail-header-img-wrap {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 0.375rem;
    border: 1px solid #e5e7eb;
    background: #f3f4f6;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}
.ref020-detail-header-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.ref020-detail-header-img-placeholder {
    color: #c0c4cc;
}
.ref020-detail-header-img-placeholder i.material-icons {
    font-size: 1.75rem;
}

/* 정보 영역 */
.ref020-detail-header-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

/* 행 공통 */
.ref020-detail-header-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0 0.625rem;
    line-height: 1.5;
}

/* 행 색상 공통 — 안건 51 C1: 값 모두 #000 (12px 본문 가독성 보장). 안건 80 (2026-05-14): swap 후 같은 색상 유지. */
.ref020-detail-header-row-product,
.ref020-detail-header-row-house {
    color: #000;
    font-size: 1rem;
    font-weight: 400;
}

/* 강조 항목 (수취인, 운송장) — 안건 48: bold 제거 / 안건 51: 색상 동일 #000.
   안건 80 정정 (2026-05-14, 사용자 피드백):
     · 파랑 강조 제거 → 아이콘/라벨 일반 톤 복귀
     · 값 부분만 bold (font-weight: 600) + 폰트 살짝 큼 (1.0625rem 유지)
     · "강조 빼고 값 부분만" 사용자 요구 정합 */
.ref020-detail-header-emph {
    font-weight: 600;
    color: #000;
    font-size: 1.0625rem;
}
.ref020-detail-header-emph i.material-icons {
    color: #1f2937; /* 일반 강조 톤 (#1f2937), 파랑 제거 */
    font-size: 1.25rem;
}
.ref020-detail-header-emph .ref020-detail-header-label {
    color: #4b5563; /* 일반 라벨 톤 */
    font-weight: 400; /* 라벨은 bold 안 함 — 값만 bold */
    font-size: 1rem;
}

/* 항목 공통 */
.ref020-detail-header-item {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    line-height: 1;
}
.ref020-detail-header-item i.material-icons {
    font-size: 1.1666rem;
    color: #4b5563; /* 안건 51: 아이콘 진한 회색 (이전 #9ca3af → #4b5563) — 12px 환경 가독성 ↑ */
    line-height: 1;
}

.ref020-detail-header-sep {
    color: #9ca3af; /* 안건 51: separator 도 한 단계 진하게 (#d1d5db → #9ca3af) */
    user-select: none;
}

/* 안건 50 (2026-05-11): 헤딩 내 외부 추적 링크 (blnoLink/trackingLink) — 색상 inherit, hover 시 밑줄.
   blnoLink 는 uk-link-reset 클래스가 붙어있지만 trackingLink 는 region 구현에 따라 다를 수 있어 명시. */
.ref020-detail-header-item a {
    color: inherit;
    text-decoration: none;
}
.ref020-detail-header-item a:hover {
    text-decoration: underline;
}

/* 안건 47: 라벨 inline — 값 앞에 prefix.
   안건 48: bold 제거.
   안건 49: 폰트 최소 1rem.
   안건 51 (2026-05-11): 라벨 색상 진하게 (#9ca3af → #4b5563) — 12px 환경에서 라벨 가독성 보장. */
.ref020-detail-header-label {
    color: #4b5563;
    font-weight: 400;
    font-size: 1rem;
    margin-right: 0.25rem;
    margin-left: 0.0625rem;
}
/* 안건 80 (2026-05-14): emph 라벨도 파랑 (.ref020-detail-header-emph 의 라벨 override 위 .ref020-detail-header-emph .ref020-detail-header-label 에서 처리) */

/* 빈값 dash — 옅은 회색 */
.ref020-detail-header-dash {
    color: #d1d5db;
    font-weight: 400;
}

/* 상품번호. 안건 51: 값 #000, # prefix 만 회색. */
.ref020-detail-header-meta-id {
    color: #000;
    white-space: nowrap;
}
.ref020-detail-header-prod-prefix {
    color: #6b7280;
}

/* 상품명 — crop. 안건 51: #000. */
.ref020-detail-header-prod-name {
    color: #000;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 24rem;
}

/* 상태 prefix 배지 — 좌상 (시안 6).
   안건 43 후속: prod-info > state.blade.php 동일 색상 (#0f7ae5 normal / #ee395b danger).
   배지 안 우측에 변경일 작게 표시. */
.ref020-detail-header-state-prefix {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
    font-size: 1rem;
    font-weight: 400;
    color: #fff;
    flex-shrink: 0;
    margin-right: 0.25rem;
    line-height: 1.2;
}
.ref020-detail-header-state-prefix i.material-icons {
    font-size: 1rem;
    color: #fff;
}
.ref020-detail-header-state-text {
    color: #fff;
}
.ref020-detail-header-state-date {
    color: rgba(255, 255, 255, 0.85);
    font-size: 1rem;
    font-weight: 400;
    padding-left: 0.375rem;
    border-left: 1px solid rgba(255, 255, 255, 0.4);
}
/* normal: 진행 중 (파랑) */
.ref020-detail-header-state-normal {
    background: #0f7ae5;
}
/* danger: 보류·취소·소각 계열 (빨강) */
.ref020-detail-header-state-danger {
    background: #ee395b;
}
/* muted (지금 안 쓰이지만 fallback) */
.ref020-detail-header-state-muted {
    background: #9ca3af;
}

/* 회원사 — 안건 51 (2026-05-11): pill 배지 제거, 다른 항목과 동일한 inline 표기.
   안건 51-B (2026-05-11): 1행 우측 끝 배치 (margin-left: auto) — 공간 분리로 "보조 정보" 시각 차별.
     · 가장 큰 화면에서는 row 1 끝에 우측 정렬
     · flex-wrap 으로 줄바꿈되면 새 줄의 우측 끝에 배치 — 보조 정보 anchor 유지 */
.ref020-detail-header-member-inline {
    color: #000;
    font-weight: 400;
    text-decoration: none;
    font-size: 1rem;
    margin-left: auto;
}
.ref020-detail-header-member-inline i.material-icons {
    color: #4b5563;
    font-size: 1.1666rem;
}
a.ref020-detail-header-member-inline:hover {
    text-decoration: underline;
}
</style>
